import React, { useCallback, useEffect, useState } from "react";

import { toast } from "react-toastify";
import { Loader2, Clock, AlertTriangle } from "lucide-react";

import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import toastError from "../../errors/toastError";
import api from "../../services/api";

const CampaignsConfig = () => {
	const [ajustes, setAjustes] = useState(null);
	const [carregando, setCarregando] = useState(true);
	const [salvando, setSalvando] = useState(false);

	const carregar = useCallback(async () => {
		setCarregando(true);
		try {
			const { data } = await api.get("/campaign-settings");
			setAjustes(data);
		} catch (err) {
			toastError(err);
		}
		setCarregando(false);
	}, []);

	useEffect(() => {
		carregar();
	}, [carregar]);

	const alterar = (campo, valor) =>
		setAjustes((atual) => ({ ...atual, [campo]: valor }));

	const salvar = async (e) => {
		e.preventDefault();
		setSalvando(true);
		try {
			const { data } = await api.put("/campaign-settings", ajustes);
			setAjustes(data);
			toast.success("Ajustes salvos. Valem para as próximas campanhas.");
		} catch (err) {
			toastError(err);
		}
		setSalvando(false);
	};

	if (carregando || !ajustes) {
		return (
			<MainContainer>
				<MainHeader>
					<Title>Config. de campanhas</Title>
				</MainHeader>
				<div className="flex-1 px-6 pb-6">
					<Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
				</div>
			</MainContainer>
		);
	}

	const temJanela = Boolean(ajustes.janelaInicio || ajustes.janelaFim);

	return (
		<MainContainer>
			<MainHeader>
				<Title>Config. de campanhas</Title>
			</MainHeader>

			<div className="flex-1 overflow-auto px-6 pb-6">
				<p className="mb-4 w-full text-sm leading-relaxed text-muted-foreground">
					O ritmo do disparo. Estes valores valem para todas as campanhas da
					empresa e existem por um motivo só: mandar rápido demais, sempre no
					mesmo intervalo e de madrugada é o que faz o WhatsApp bloquear um
					número.
				</p>

				<form
					onSubmit={salvar}
					className="w-full space-y-5 rounded-xl border bg-card p-5"
				>
					<div>
						<Label htmlFor="intervaloPadrao">
							Intervalo entre um envio e outro
						</Label>
						<div className="mt-1 flex items-center gap-2">
							<Input
								id="intervaloPadrao"
								type="number"
								min="1"
								className="w-28"
								value={ajustes.intervaloPadrao}
								onChange={(e) => alterar("intervaloPadrao", e.target.value)}
							/>
							<span className="text-sm text-muted-foreground">segundos</span>
						</div>
						<p className="mt-1 text-xs leading-relaxed text-muted-foreground">
							Vem preenchido ao criar uma campanha nova; cada campanha ainda
							pode ter o seu. Abaixo de 10 segundos o risco de bloqueio sobe
							bastante.
						</p>
					</div>

					<div className="border-t pt-4">
						<Label htmlFor="pausaACada">Parar para respirar</Label>
						<div className="mt-1 flex flex-wrap items-center gap-2">
							<span className="text-sm text-muted-foreground">a cada</span>
							<Input
								id="pausaACada"
								type="number"
								min="0"
								className="w-24"
								value={ajustes.pausaACada}
								onChange={(e) => alterar("pausaACada", e.target.value)}
							/>
							<span className="text-sm text-muted-foreground">
								envios, esperar
							</span>
							<Input
								id="pausaSegundos"
								type="number"
								min="0"
								className="w-24"
								value={ajustes.pausaSegundos}
								onChange={(e) => alterar("pausaSegundos", e.target.value)}
							/>
							<span className="text-sm text-muted-foreground">segundos</span>
						</div>
						<p className="mt-1 text-xs leading-relaxed text-muted-foreground">
							Zero desliga a pausa. Um ritmo perfeitamente constante é o que
							mais parece robô — a parada periódica imita quem manda de fato.
						</p>
					</div>

					<div className="border-t pt-4">
						<Label className="flex items-center gap-1.5">
							<Clock className="h-3.5 w-3.5" />
							Só disparar entre
						</Label>
						<div className="mt-1 flex flex-wrap items-center gap-2">
							<Input
								type="time"
								className="w-32"
								value={ajustes.janelaInicio}
								onChange={(e) => alterar("janelaInicio", e.target.value)}
							/>
							<span className="text-sm text-muted-foreground">e</span>
							<Input
								type="time"
								className="w-32"
								value={ajustes.janelaFim}
								onChange={(e) => alterar("janelaFim", e.target.value)}
							/>
							{temJanela && (
								<Button
									type="button"
									variant="ghost"
									size="sm"
									onClick={() => {
										alterar("janelaInicio", "");
										alterar("janelaFim", "");
									}}
								>
									Limpar
								</Button>
							)}
						</div>
						<p className="mt-1 text-xs leading-relaxed text-muted-foreground">
							Em branco nas duas pontas, dispara a qualquer hora. Fora da
							janela a campanha para sozinha e continua de onde ficou quando o
							horário voltar — nenhum contato se perde.
						</p>
					</div>

					<p className="flex gap-1.5 rounded-md bg-muted p-3 text-xs leading-relaxed">
						<AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
						Vale para campanhas que ainda vão rodar. Uma campanha já em
						andamento continua no ritmo com que começou até terminar.
					</p>

					<Button type="submit" disabled={salvando}>
						{salvando && <Loader2 className="h-4 w-4 animate-spin" />}
						Salvar ajustes
					</Button>
				</form>
			</div>
		</MainContainer>
	);
};

export default CampaignsConfig;
